import { getCountries, getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js'

/**
 * Nation selector data for phone inputs (T11.3), from libphonenumber-js —
 * compiled from the same Google libphonenumber metadata as the backend's
 * `phonenumber` crate, so the lists can only drift by library version.
 * Display-only: the backend stays the authority on validity (B6) — the
 * selector just supplies the ISO code, and the dial code shown is a reading
 * aid.
 */

/** Browser locale's region when it has a numbering plan, else VN (the launch market). */
export function defaultNation(): string {
  try {
    const region = new Intl.Locale(navigator.language).maximize().region
    if (region && isSupportedCountry(region)) return region
  } catch {
    // Unparsable locale: fall through.
  }
  return 'VN'
}

export interface NationOption {
  code: string
  label: string
}

const cache = new Map<string, NationOption[]>()

/** Options localized to the UI language, sorted by name; codes label themselves when unknown. */
export function nationOptions(language: string): NationOption[] {
  const cached = cache.get(language)
  if (cached) return cached
  let names: Intl.DisplayNames | undefined
  let collator: Intl.Collator
  try {
    names = new Intl.DisplayNames([language], { type: 'region' })
    collator = new Intl.Collator(language)
  } catch {
    // No region names for this runtime/language: codes still work.
    collator = new Intl.Collator()
  }
  const options = getCountries()
    .map((code) => ({ code, label: `${names?.of(code) ?? code} (+${getCountryCallingCode(code)})` }))
    .sort((a, b) => collator.compare(a.label, b.label))
  cache.set(language, options)
  return options
}
